import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthenticationService, ConfigurationService, HistoryService, LocaleService, MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { Application as EngineApplication } from '@philips-internal/oswald-ui-engine';
import { getViewportElement, isContentfulString } from '@philips-internal/oswald-ui-utils';
import { applicationContext, foundationContext } from '../../context/index.js';
import { useBrowsingContext } from '../../hooks/use-browsing-context.js';
import { useOpenIDConnect } from '../../hooks/use-openid-connect.js';
import { doesRouteMatch, isLastActiveTimeLongerThan, requestInformation, setLastActiveTime } from '../../util/index.js';
import { FoundationLoader } from '../foundation-loader/index.js';
import { IdleTimer } from '../idle-timer/index.js';
function foundationApplicationError(message) {
    return new Error(`[Foundation Application]: ${message}`);
}
function FoundationApplication(props) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { inactivity, openIDConnect, routes } = ConfigurationService.getAuthenticationConfiguration() || {};
    if (!routes?.login) {
        throw foundationApplicationError('Missing configuration for login route.');
    }
    const [loading, setLoading] = useState(true);
    const [isInformationLoaded, setIsInformationLoaded] = useState(false);
    const [key, setKey] = useState(Number.NEGATIVE_INFINITY);
    const fifteenMinutes = 15 * 60 * 1000;
    const maxIdleTime = inactivity?.time || fifteenMinutes;
    useBrowsingContext();
    const isOpenIDConnectLoading = useOpenIDConnect();
    useEffect(() => {
        if (!AuthenticationService.isAuthenticated() || !foundationContext.isEmpty()) {
            return;
        }
        setLoading(true);
        const abortController = new AbortController();
        void requestInformation(abortController, false).then(() => {
            setLoading(false);
            setIsInformationLoaded(true);
        });
        return () => {
            abortController.abort();
        };
    }, [location]);
    useEffect(() => {
        const event = 'onbeforeload';
        const listener = () => setLastActiveTime();
        window.addEventListener(event, listener);
        if (isLastActiveTimeLongerThan(maxIdleTime)) {
            onIdle();
        }
        return () => {
            window.removeEventListener(event, listener);
        };
    }, []);
    useEffect(() => {
        const unlistenLogout = AuthenticationService.listenLogout(() => {
            /** As the user can change on the fly without unmounting the foundation application
             * this setIsInformationAlreadyRequested reset to false is needed
             */
            setIsInformationLoaded(false);
            applicationContext.closeModules();
            foundationContext.clearInformation();
        });
        const unlistenLocales = LocaleService.addListener(() => setKey(Math.random()));
        const unlisteMessages = MessageService.addListener(() => setKey(Math.random()));
        return () => {
            unlistenLogout();
            unlistenLocales();
            unlisteMessages();
        };
    }, []);
    const whitelistedRoutes = { ...routes, callbackRoute: openIDConnect?.callbackRoute, errorRoute: openIDConnect?.errorRoute, home: null };
    const isMatchingRoute = doesRouteMatch(location.pathname + location.search, routes?.custom);
    const isWhitelistedRoute = Object.values(whitelistedRoutes)
        .filter(Boolean)
        .flat()
        .includes(location.pathname);
    const isAllowedRoute = isWhitelistedRoute || isMatchingRoute;
    const isLoginRoute = location.pathname === routes.login;
    const isLoginRouteWithoutState = isLoginRoute && !isContentfulString(searchParams.get('state'));
    const isRootPath = location.pathname === '/';
    const shouldShowLoader = AuthenticationService.isAuthenticated()
        ? !isLoginRoute && (!isInformationLoaded || loading)
        : isLoginRouteWithoutState || isOpenIDConnectLoading || loading && !isAllowedRoute;
    if (shouldShowLoader) {
        return React.createElement(FoundationLoader, null);
    }
    if (AuthenticationService.isAuthenticated() && (isLoginRoute || isRootPath)) {
        const route = HistoryService.makeURL(routes.home);
        return React.createElement(Navigate, { to: route });
    }
    const onIdle = () => {
        void AuthenticationService.logout().then(() => {
            AuthenticationService.redirectToInactivity();
        });
    };
    return (React.createElement("div", { className: "c-foundation-application u-fill-available-space", key: key },
        React.createElement("div", { className: "c-foundation-application__content u-fill-available-space" },
            React.createElement(EngineApplication, { engine: props.engine, fragment: props.rootFragmentName })),
        !isAllowedRoute && (React.createElement(IdleTimer, { element: getViewportElement(), onIdle: onIdle, timeout: maxIdleTime }))));
}
export { FoundationApplication, };
//# sourceMappingURL=foundation-application.js.map